import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, User, Clock, AlertCircle, Loader2 } from 'lucide-react';

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  created_at: string;
}

interface Conversation {
  id: string;
  client_id: string;
  advisor_id: string;
  status: string;
  advisor: {
    display_name: string;
    profile_image_url: string | null;
  };
  client: {
    display_name: string;
    profile_image_url: string | null;
  };
}

const PastConversationViewer: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!conversationId) {
      navigate('/client/dashboard');
      return;
    }

    const fetchConversationAndMessages = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch the conversation details
        const { data: conversationData, error: conversationError } = await supabase
          .from('conversations')
          .select(`
            id,
            client_id,
            advisor_id,
            status,
            advisor:advisor_id (
              display_name,
              profile_image_url
            ),
            client:client_id (
              display_name,
              profile_image_url
            )
          `)
          .eq('id', conversationId)
          .single();

        if (conversationError) {
          throw new Error('Failed to load conversation details');
        }

        if (!conversationData) {
          throw new Error('Conversation not found');
        }

        // Check if the user is part of this conversation
        if (conversationData.client_id !== user.id && conversationData.advisor_id !== user.id) {
          throw new Error('You do not have permission to view this conversation');
        }

        setConversation(conversationData);

        // Fetch messages for this conversation
        const { data: messagesData, error: messagesError } = await supabase
          .from('messages')
          .select('*')
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: true });

        if (messagesError) {
          throw new Error('Failed to load messages');
        }

        setMessages(messagesData || []);
      } catch (err) {
        console.error('Error fetching conversation:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchConversationAndMessages();
  }, [user, conversationId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading conversation history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="flex items-center text-red-600 mb-4">
            <AlertCircle className="w-6 h-6 mr-2" />
            <h2 className="text-xl font-semibold">Error</h2>
          </div>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={() => navigate('/client/dashboard')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors w-full"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <p className="text-gray-600">Conversation not found.</p>
          <button
            onClick={() => navigate('/client/dashboard')}
            className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isClient = user?.id === conversation.client_id;
  const otherParty = isClient ? conversation.advisor : conversation.client;

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <button
          onClick={() => navigate('/client/dashboard')}
          className="mb-6 flex items-center text-purple-600 hover:text-purple-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Conversation Header */}
          <div className="bg-purple-600 text-white p-4 flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center overflow-hidden">
                {otherParty.profile_image_url ? (
                  <img 
                    src={otherParty.profile_image_url} 
                    alt={otherParty.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="ml-3">
                <p className="font-medium">{otherParty.display_name}</p>
                <p className="text-xs text-purple-200">
                  {conversation.status === 'active' ? 'Active Chat' : 'Past Conversation'}
                </p>
              </div>
            </div>
            <div className="flex items-center text-sm">
              <Clock className="w-4 h-4 mr-1" />
              <span>
                {messages.length > 0 
                  ? new Date(messages[messages.length - 1].created_at).toLocaleDateString() 
                  : new Date().toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Messages */}
          <div className="p-4 h-[60vh] overflow-y-auto bg-gray-50">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <p>No messages in this conversation</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.sender_id === user?.id
                          ? 'bg-purple-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-800'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      <div
                        className={`text-xs mt-1 ${
                          message.sender_id === user?.id ? 'text-purple-200' : 'text-gray-500'
                        }`}
                      >
                        {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-white">
            <div className="text-center text-gray-500">
              <p>This is a past conversation. {conversation.status === 'closed' ? 'The chat has ended.' : ''}</p>
              <button
                onClick={() => navigate('/advisors')}
                className="mt-4 bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Find Another Advisor
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PastConversationViewer;